import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Chip, IconButton, 
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, FormControl, InputLabel, Select, 
  MenuItem, Stack, Card, CardContent, Divider,
  Stepper, Step, StepLabel,
  Accordion, AccordionSummary, AccordionDetails, Tooltip,
  RadioGroup, FormControlLabel, Radio, FormLabel
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Science as ScienceIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Share as ShareIcon,
  DragIndicator as DragIcon,
  Compare as CompareIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { getMyWaxTests, deleteWaxTest, createWaxTest, addSkiToWaxTest, updateWaxTest, getWaxTest, getWaxTestSharedTeams, recordWaxTestComparison, getNextWaxTestComparisons, updateSkiInWaxTest, getCoachSkiFleet } from '../api';
import ShareWaxTestModal from './ShareWaxTestModal';
import WeatherDataForm from '../components/WeatherDataForm';
import WeatherDataDisplay from '../components/WeatherDataDisplay';
import WeatherFilter from '../components/WeatherFilter';
import { filterByWeatherConditions, hasActiveWeatherFilters } from '../utils/weatherFilterUtils';

const steps = ['Setup Test', 'Add Skis', 'Choose Method', 'Rank Results'];

export default function WaxTestsSection() {
  const [tests, setTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [sharedMap, setSharedMap] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [currentTest, setCurrentTest] = useState(null);
  const [skis, setSkis] = useState([]);
  const [rankingMethod, setRankingMethod] = useState('manual');
  const [currentComparison, setCurrentComparison] = useState(null);
  const [winner, setWinner] = useState(null);
  const [distance, setDistance] = useState('');
  const [weatherFilters, setWeatherFilters] = useState({});
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    // Standardized weather fields
    snow_temperature_c: '',
    air_temperature_c: '',
    snow_humidity_doser: '',
    air_humidity_rh: '',
    clouds_sky_parts: '',
    visibility: '',
    wind: '',
    precipitation: '',
    artificial_snow: '',
    natural_snow: '',
    grain_size: '',
    snow_humidity: '',
    track_hardness: '',
    track_shininess: '',
    track_dirtiness: '',
    // Test configuration
    technique: 'skate',
    test_type: 'glide',
    notes: ''
  });

  useEffect(() => {
    loadTests();
  }, []);

  // Apply weather filters whenever tests or filters change
  useEffect(() => {
    if (hasActiveWeatherFilters(weatherFilters)) {
      const filtered = filterByWeatherConditions(tests, weatherFilters);
      setFilteredTests(filtered);
    } else {
      setFilteredTests(tests);
    }
  }, [tests, weatherFilters]);

  const handleWeatherFilterChange = (filters) => {
    setWeatherFilters(filters);
  };

  const handleClearWeatherFilters = () => {
    setWeatherFilters({});
  };

  const loadTests = async () => {
    try {
      setLoading(true);
      const data = await getMyWaxTests();
      setTests(data);
      setFilteredTests(data);
      setError(null);
      // Fetch shared status for each test
      const sharedStatuses = await Promise.all(
        data.map(async (test) => {
          const teams = await getWaxTestSharedTeams(test.id);
          return { id: test.id, shared: teams.length > 0 };
        })
      );
      const sharedMapObj = {};
      sharedStatuses.forEach(({ id, shared }) => {
        sharedMapObj[id] = shared;
      });
      setSharedMap(sharedMapObj);
    } catch (err) {
      setError('Failed to load wax tests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const testData = {
        ...formData,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
        humidity: formData.humidity ? parseFloat(formData.humidity) : null,
      };

      if (editingTest) {
        // Update existing test
        await updateWaxTest(editingTest.id, testData);
      } else {
        // Create new test
        const newTest = await createWaxTest(testData);
        setCurrentTest(newTest);
        setCurrentStep(2);
        setSkis([]);
        setDialogOpen(false);
        return;
      }

      setDialogOpen(false);
      setEditingTest(null);
      resetForm();
      loadTests();
    } catch (err) {
      setError('Failed to save wax test');
      console.error(err);
    }
  };

  const handleEdit = (test) => {
    setEditingTest(test);
    setFormData({
      title: test.title || '',
      location: test.location || '',
      temperature: test.temperature?.toString() || '',
      humidity: test.humidity?.toString() || '',
      snow_conditions: test.snow_conditions || '',
      snow_type: test.snow_type || '',
      air_conditions: test.air_conditions || '',
      technique: test.technique || 'skate',
      test_type: test.test_type || 'glide',
      notes: test.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (testId) => {
    if (window.confirm('Are you sure you want to delete this wax test?')) {
      try {
        await deleteWaxTest(testId);
        loadTests();
      } catch (err) {
        setError('Failed to delete wax test');
        console.error(err);
      }
    }
  };

  const handleShare = (test) => {
    setSelectedTest(test);
    setShareModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      location: '',
      temperature: '',
      humidity: '',
      snow_conditions: '',
      snow_type: '',
      air_conditions: '',
      technique: 'skate',
      test_type: 'glide',
      notes: ''
    });
  };

  const openDialog = () => {
    setEditingTest(null);
    resetForm();
    setDialogOpen(true);
  };

  const addSki = async (skiData) => {
    try {
      await addSkiToWaxTest(currentTest.id, skiData);
      const updatedTest = await getWaxTest(currentTest.id);
      setCurrentTest(updatedTest);
      setSkis(updatedTest.skis || []);
    } catch (err) {
      setError('Failed to add ski');
    }
  };

  const completeTest = async () => {
    try {
      await updateWaxTest(currentTest.id, { status: 'completed' });
      setCurrentTest(null);
      setCurrentStep(1);
      setSkis([]);
      loadTests();
    } catch (err) {
      setError('Failed to complete test');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'setup': return 'default';
      case 'skis_added': return 'primary';
      case 'ranking': return 'warning';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'setup': return <ScheduleIcon />;
      case 'skis_added': return <AddIcon />;
      case 'ranking': return <PlayIcon />;
      case 'completed': return <CheckIcon />;
      default: return <ScheduleIcon />;
    }
  };

  const getTestTypeColor = (testType) => {
    switch (testType) {
      case 'grip': return 'error';
      case 'glide': return 'info';
      default: return 'default';
    }
  };

  const getTechniqueColor = (technique) => {
    switch (technique) {
      case 'classic': return 'success';
      case 'skate': return 'primary';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'setup': return 'Setup';
      case 'skis_added': return 'Skis Added';
      case 'ranking': return 'Ranking';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If we're in the middle of creating a test, show the step-by-step interface
  if (currentTest) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Button
            onClick={() => {
              setCurrentTest(null);
              setCurrentStep(1);
              setSkis([]);
            }}
            sx={{ mb: 2 }}
          >
            ← Back to Wax Tests
          </Button>
          
          <Typography variant="h4" component="h1" gutterBottom>
            {currentTest.title}
          </Typography>
          
          <Stepper activeStep={currentStep - 1} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {currentStep === 2 && (
          <AddSkisStep 
            test={currentTest} 
            skis={skis} 
            onAddSki={addSki}
            onNext={() => setCurrentStep(3)}
            onCancel={() => {
              setCurrentTest(null);
              setCurrentStep(1);
              setSkis([]);
            }}
          />
        )}

        {currentStep === 3 && (
          <ChooseMethodStep 
            test={currentTest}
            skis={skis}
            rankingMethod={rankingMethod}
            setRankingMethod={setRankingMethod}
            onNext={() => setCurrentStep(4)}
            onCancel={() => {
              setCurrentTest(null);
              setCurrentStep(1);
              setSkis([]);
            }}
          />
        )}

        {currentStep === 4 && (
          <RankingStep 
            test={currentTest}
            skis={skis}
            rankingMethod={rankingMethod}
            currentComparison={currentComparison}
            setCurrentComparison={setCurrentComparison}
            winner={winner}
            setWinner={setWinner}
            distance={distance}
            setDistance={setDistance}
            onComplete={completeTest}
            onCancel={() => {
              setCurrentTest(null);
              setCurrentStep(1);
              setSkis([]);
            }}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScienceIcon />
          Wax Tests
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openDialog}
        >
          Add Entry
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Weather Filter */}
      <WeatherFilter 
        onFilterChange={handleWeatherFilterChange}
        onClearFilters={handleClearWeatherFilters}
        compact={false}
        title="Filter Wax Tests by Weather Conditions"
      />

      {filteredTests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ScienceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {tests.length === 0 ? 'No wax tests yet' : 'No wax tests match your weather filters'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {tests.length === 0 
              ? 'Start testing different wax combinations to find the best performance for your conditions.'
              : 'Try adjusting your weather filters to see more wax tests.'
            }
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={tests.length === 0 ? openDialog : handleClearWeatherFilters}
          >
            {tests.length === 0 ? 'Create Your First Test' : 'Clear Weather Filters'}
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {filteredTests.map((test) => (
            <Paper key={test.id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {test.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {new Date(test.date).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={test.technique}
                    color={getTechniqueColor(test.technique)}
                    size="small"
                  />
                  <Chip
                    label={test.test_type}
                    color={getTestTypeColor(test.test_type)}
                    size="small"
                  />
                  <Chip
                    icon={getStatusIcon(test.status)}
                    label={getStatusText(test.status)}
                    color={getStatusColor(test.status)}
                    size="small"
                  />
                  {sharedMap[test.id] && (
                    <Tooltip title="Shared with one or more teams">
                      <Chip label="Shared" color="success" size="small" />
                    </Tooltip>
                  )}
                  <IconButton size="small" onClick={() => handleShare(test)}>
                    <ShareIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleEdit(test)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(test.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">View Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>Location</Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {test.location || 'N/A'}
                      </Typography>
          <WeatherDataDisplay
            weatherData={test}
            title="Weather Conditions"
            compact={true}
          />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>Test Info</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Skis:</strong> {test.ski_count || 0}
                        </Typography>
                        {test.status === 'completed' && (
                          <Typography variant="body2">
                            <strong>Ranked:</strong> {test.ranked_skis || 0}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          <strong>Method:</strong> {test.ranking_method || 'Not set'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>Ranking Results</Typography>
                      <Box sx={{ mb: 2 }}>
                        {test.status === 'completed' && test.ranked_skis > 0 ? (
                          <RankingDiagram test={test} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {test.status === 'completed' ? 'No ranking data available' : 'Test not completed yet'}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    {test.notes && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" gutterBottom>Notes</Typography>
                        <Typography variant="body2">{test.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Share Modal */}
      <ShareWaxTestModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        test={selectedTest}
      />

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTest ? 'Edit Wax Test' : 'Add Wax Test Entry'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Test Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Technique</InputLabel>
                  <Select
                    value={formData.technique}
                    onChange={(e) => setFormData({ ...formData, technique: e.target.value })}
                    label="Technique"
                  >
                    <MenuItem value="skate">Skate</MenuItem>
                    <MenuItem value="classic">Classic</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Test Type</InputLabel>
                  <Select
                    value={formData.test_type}
                    onChange={(e) => setFormData({ ...formData, test_type: e.target.value })}
                    label="Test Type"
                  >
                    <MenuItem value="glide">Glide Test</MenuItem>
                    <MenuItem value="grip">Grip Test</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
            </Grid>
            
            {/* Weather Data Form */}
          <WeatherDataForm
            weatherData={formData}
            onChange={setFormData}
            title="Weather Conditions"
            compact={true}
          />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingTest ? 'Update' : 'Add'} Entry
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

// Add Skis Step Component
function AddSkisStep({ test, skis, onAddSki, onNext, onCancel }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fleetSkiMode, setFleetSkiMode] = useState('random'); // 'random' or 'fleet'
  const [fleetSkis, setFleetSkis] = useState([]);
  const [selectedFleetSki, setSelectedFleetSki] = useState(null);
  const [formData, setFormData] = useState({
    ski_name: '',
    wax_description: '',
    ski_comment: ''
  });

  useEffect(() => {
    loadFleetSkis();
  }, []);

  const loadFleetSkis = async () => {
    try {
      const data = await getCoachSkiFleet();
      setFleetSkis(data);
    } catch (err) {
      console.error('Error loading fleet skis:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const skiDataToSubmit = { ...formData };
    
    // If using fleet ski, auto-fill the ski name with fleet ski info
    if (fleetSkiMode === 'fleet' && selectedFleetSki) {
      skiDataToSubmit.ski_name = selectedFleetSki.ski_name;
      // Include additional info in the comment if there's already a comment
      if (selectedFleetSki.brand || selectedFleetSki.serial_number) {
        const fleetInfo = [selectedFleetSki.brand, selectedFleetSki.serial_number].filter(Boolean).join(' - ');
        skiDataToSubmit.ski_comment = skiDataToSubmit.ski_comment 
          ? `${skiDataToSubmit.ski_comment}\n(Fleet: ${fleetInfo})`
          : `Fleet: ${fleetInfo}`;
      }
    }
    
    await onAddSki(skiDataToSubmit);
    setFormData({ ski_name: '', wax_description: '', ski_comment: '' });
    setSelectedFleetSki(null);
    setFleetSkiMode('random');
    setDialogOpen(false);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Step 2: Add Skis to Test
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Add the skis you want to test. For each ski, specify the wax combination you're testing.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Add Ski
        </Button>
      </Box>

      {skis.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No skis added yet
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Add at least 2 skis to start your wax test.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Add Your First Ski
          </Button>
        </Box>
      ) : (
        <Stack spacing={2}>
          {skis.map((ski) => (
            <Card key={ski.id}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {ski.ski_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Wax:</strong> {ski.wax_description}
                </Typography>
                {ski.ski_comment && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Note:</strong> {ski.ski_comment}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={skis.length < 2}
        >
          Next: Rank Results
        </Button>
      </Box>

      {/* Add Ski Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Ski to Test</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              {/* Mode Selection */}
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Select Ski Source</FormLabel>
                  <RadioGroup
                    row
                    value={fleetSkiMode}
                    onChange={(e) => {
                      setFleetSkiMode(e.target.value);
                      setSelectedFleetSki(null);
                      setFormData({ ...formData, ski_name: '' });
                    }}
                  >
                    <FormControlLabel value="random" control={<Radio />} label="Add Random Ski" />
                    <FormControlLabel value="fleet" control={<Radio />} label="From My Fleet" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Fleet Ski Selection */}
              {fleetSkiMode === 'fleet' && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Select Ski from Fleet</InputLabel>
                    <Select
                      value={selectedFleetSki?.id || ''}
                      onChange={(e) => {
                        const ski = fleetSkis.find(s => s.id === e.target.value);
                        setSelectedFleetSki(ski);
                        if (ski) {
                          setFormData({ ...formData, ski_name: ski.ski_name });
                        }
                      }}
                      label="Select Ski from Fleet"
                      sx={{ 
                        minWidth: 300,
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word'
                        }
                      }}
                    >
                      {fleetSkis.map((ski) => (
                        <MenuItem 
                          key={ski.id} 
                          value={ski.id}
                          sx={{ 
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            py: 1.5,
                            minHeight: 'auto'
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {ski.ski_name}
                            </Typography>
                            {(ski.brand || ski.serial_number) && (
                              <Typography variant="caption" color="text.secondary">
                                {[ski.brand, ski.serial_number].filter(Boolean).join(' - ')}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Random Ski Name - only show when not using fleet */}
              {fleetSkiMode === 'random' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Ski Name/Number"
                    value={formData.ski_name}
                    onChange={(e) => setFormData({ ...formData, ski_name: e.target.value })}
                    required
                    placeholder="e.g., Ski 1, Test Ski A, etc."
                  />
                </Grid>
              )}
              
              {/* Show selected fleet ski info */}
              {fleetSkiMode === 'fleet' && selectedFleetSki && (
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: 'action.selected', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Selected Ski:</Typography>
                    <Typography variant="body2">{selectedFleetSki.ski_name}</Typography>
                    {(selectedFleetSki.brand || selectedFleetSki.serial_number) && (
                      <Typography variant="caption" color="text.secondary">
                        {[selectedFleetSki.brand, selectedFleetSki.serial_number].filter(Boolean).join(' - ')}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Wax Description"
                  value={formData.wax_description}
                  onChange={(e) => setFormData({ ...formData, wax_description: e.target.value })}
                  required
                  multiline
                  rows={2}
                    placeholder="e.g., Swix LF4 + LF6, Toko Red + Blue, etc."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ski Comment (Optional)"
                  value={formData.ski_comment}
                  onChange={(e) => setFormData({ ...formData, ski_comment: e.target.value })}
                  multiline
                  rows={2}
                  placeholder="Any additional notes about this ski or wax combination..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setDialogOpen(false);
              setFormData({ ski_name: '', wax_description: '', ski_comment: '' });
              setSelectedFleetSki(null);
              setFleetSkiMode('random');
            }}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={!formData.wax_description || (fleetSkiMode === 'random' && !formData.ski_name)}
            >
              Add Ski
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Paper>
  );
}

// Choose Method Step Component
function ChooseMethodStep({ test, skis, rankingMethod, setRankingMethod, onNext, onCancel }) {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Step 3: Choose Ranking Method
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        How would you like to rank the skis? Choose the method that works best for your testing setup.
      </Typography>

      <FormControl component="fieldset" sx={{ width: '100%' }}>
        <FormLabel component="legend">Ranking Method</FormLabel>
        <RadioGroup
          value={rankingMethod}
          onChange={(e) => setRankingMethod(e.target.value)}
          sx={{ mt: 1 }}
        >
          <Paper sx={{ p: 2, mb: 2, border: rankingMethod === 'manual' ? '2px solid' : '1px solid', borderColor: rankingMethod === 'manual' ? 'primary.main' : 'divider' }}>
            <FormControlLabel 
              value="manual" 
              control={<Radio />} 
              label={
                <Box>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DragIcon />
                    Manual Ranking
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Drag and drop skis in order of performance and enter distance gaps between them.
                    Best for when you can test all skis at once or have a clear ranking.
                  </Typography>
                </Box>
              }
              sx={{ width: '100%', alignItems: 'flex-start' }}
            />
          </Paper>

          <Paper sx={{ p: 2, border: rankingMethod === 'automatic' ? '2px solid' : '1px solid', borderColor: rankingMethod === 'automatic' ? 'primary.main' : 'divider' }}>
            <FormControlLabel 
              value="automatic" 
              control={<Radio />} 
              label={
                <Box>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CompareIcon />
                    Automatic Pairwise Comparison
                    <Chip
                        label="BETA"
                        color="warning"
                        size="small"
                        sx={{
                        fontWeight: 'bold',
                        letterSpacing: 1,
                        ml: 1,
                        verticalAlign: 'middle'
                        }}
                    />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compare skis two at a time. The app will guide you through systematic comparisons
                    to determine the final ranking. Best for methodical testing.
                  </Typography>
                </Box>
              }
              sx={{ width: '100%', alignItems: 'flex-start' }}
            />
          </Paper>
        </RadioGroup>
      </FormControl>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onNext}
        >
          Next: Start {rankingMethod === 'manual' ? 'Manual' : 'Automatic'} Ranking
        </Button>
      </Box>
    </Paper>
  );
}

// Ranking Step Component
function RankingStep({ test, skis, rankingMethod, currentComparison, setCurrentComparison, winner, setWinner, distance, setDistance, onComplete, onCancel }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [rankedSkis, setRankedSkis] = useState([]);

  const loadNextComparison = useCallback(async () => {
    try {
      const comparisons = await getNextWaxTestComparisons(test.id);
      if (comparisons.length > 0) {
        setCurrentComparison(comparisons[0]);
        setWinner(null);
        setDistance('');
      } else {
        // No more comparisons, test is complete
        await updateWaxTest(test.id, { status: 'completed' });
        onComplete();
      }
    } catch (err) {
      setError('Failed to load next comparison');
      console.error(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [test.id, onComplete]);

  useEffect(() => {
    if (rankingMethod === 'automatic') {
      loadNextComparison();
    } else {
      // For manual ranking, initialize with current skis and set initial ranks
      const skisWithRanks = skis.map((ski, index) => ({
        ...ski,
        rank: ski.rank || (index + 1) // Use existing rank or set based on position
      }));
      setRankedSkis(skisWithRanks);
    }
  }, [rankingMethod, skis, loadNextComparison]);

  const submitComparison = async () => {
    if (!winner) {
      setError('Please select which ski performed better');
      return;
    }

    if (test.test_type === 'glide' && !distance) {
      setError('Please enter the distance difference for glide tests');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await recordWaxTestComparison(test.id, currentComparison.id, {
        winner_id: winner,
        distance_difference: test.test_type === 'glide' ? parseFloat(distance) : null
      });

      loadNextComparison();
    } catch (err) {
      setError('Failed to record comparison');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const moveSki = (fromIndex, toIndex) => {
    const newSkis = [...rankedSkis];
    const [movedSki] = newSkis.splice(fromIndex, 1);
    newSkis.splice(toIndex, 0, movedSki);
    
    // Update ranks
    const updatedSkis = newSkis.map((ski, index) => ({
      ...ski,
      rank: index + 1
    }));
    
    setRankedSkis(updatedSkis);
  };

  const saveManualRanking = async () => {
    try {
      setSaving(true);
      setError(null);

      // Save all ski rankings
      for (const ski of rankedSkis) {
        await updateSkiInWaxTest(test.id, ski.id, {
          rank: ski.rank,
          distance_behind_previous: ski.distance_behind_previous
        });
      }

      // Mark test as completed
      await updateWaxTest(test.id, { 
        ranking_method: 'manual',
        status: 'completed' 
      });
      
      onComplete();
    } catch (err) {
      setError('Failed to save ranking');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (rankingMethod === 'manual') {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 4: Manual Ranking
        </Typography>
        
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Drag the skis below to rank them from best (1st place) to worst. 
            Enter the distance between each pair of skis in centimeters in the input boxes.
          </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          {rankedSkis.map((ski, index) => (
            <React.Fragment key={ski.id}>
              {/* Ski Card */}
              <Card 
                sx={{ 
                  position: 'relative',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  cursor: 'move',
                  '&:hover': {
                    boxShadow: 3
                  }
                }}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', index.toString());
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
                  moveSki(draggedIndex, index);
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <DragIcon color="action" sx={{ cursor: 'grab' }} />
                    <Typography variant="h6" color="primary" sx={{ minWidth: 60 }}>
                      #{index + 1}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {ski.ski_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Wax:</strong> {ski.wax_description}
                      </Typography>
                      {ski.ski_comment && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Note:</strong> {ski.ski_comment}
                        </Typography>
                      )}
                    </Box>
                    {index === 0 && (
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                        🏆 Winner
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Distance Input Between Skis */}
              {index < rankedSkis.length - 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 1,
                  position: 'relative'
                }}>
                  <Box sx={{ 
                    width: '100%', 
                    height: 2, 
                    backgroundColor: 'divider',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <TextField
                      size="small"
                      type="number"
                      label={`Distance behind #${index + 1} (cm)`}
                      value={ski.distance_behind_previous || ''}
                      onChange={(e) => {
                        const newSkis = [...rankedSkis];
                        const skiIndex = newSkis.findIndex(s => s.id === ski.id);
                        newSkis[skiIndex].distance_behind_previous = parseFloat(e.target.value) || null;
                        setRankedSkis(newSkis);
                      }}
                            inputProps={{ step: "1", min: "0" }}
                            sx={{ 
                              width: 200,
                              backgroundColor: 'background.paper',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'background.paper'
                              }
                            }}
                            placeholder="e.g., 250"
                    />
                  </Box>
                </Box>
              )}
            </React.Fragment>
          ))}
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveManualRanking}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Complete Ranking'}
          </Button>
        </Box>
      </Paper>
    );
  }

  // Automatic ranking
  if (!currentComparison) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 4: Automatic Ranking
        </Typography>
        <Alert severity="info">Loading next comparison...</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Step 4: Automatic Ranking
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Test these two skis and tell us which one performed better.
        {test.test_type === 'glide' && ' For glide tests, also enter how much better the winning ski was.'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              border: winner === currentComparison.ski_1_id ? '2px solid' : '1px solid',
              borderColor: winner === currentComparison.ski_1_id ? 'primary.main' : 'divider',
              cursor: 'pointer'
            }}
            onClick={() => setWinner(currentComparison.ski_1_id)}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {currentComparison.ski_1_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Wax:</strong> {currentComparison.ski_1_wax}
              </Typography>
              {winner === currentComparison.ski_1_id && (
                <Typography variant="body2" color="primary.main" sx={{ mt: 1, fontWeight: 'bold' }}>
                  ✓ Selected
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              border: winner === currentComparison.ski_2_id ? '2px solid' : '1px solid',
              borderColor: winner === currentComparison.ski_2_id ? 'primary.main' : 'divider',
              cursor: 'pointer'
            }}
            onClick={() => setWinner(currentComparison.ski_2_id)}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {currentComparison.ski_2_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Wax:</strong> {currentComparison.ski_2_wax}
              </Typography>
              {winner === currentComparison.ski_2_id && (
                <Typography variant="body2" color="primary.main" sx={{ mt: 1, fontWeight: 'bold' }}>
                  ✓ Selected
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {test.test_type === 'glide' && winner && (
        <Box sx={{ mt: 3 }}>
          <TextField
            fullWidth
            type="number"
            label="Distance difference (cm)"
            value={distance}
            onChange={(e) => setDistance(e.target.value)}
            placeholder="e.g., 250"
            inputProps={{ step: "1", min: "0" }}
            helperText="How many centimeters ahead was the winning ski?"
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submitComparison}
          disabled={saving || !winner || (test.test_type === 'glide' && !distance)}
        >
          {saving ? 'Recording...' : 'Record Result'}
        </Button>
      </Box>
    </Paper>
  );
}

// Ranking Diagram Component
function RankingDiagram({ test }) {
  const [skis, setSkis] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTestSkis = useCallback(async () => {
    try {
      const testData = await getWaxTest(test.id);
      setSkis(testData.skis || []);
    } catch (err) {
      console.error('Failed to load test skis:', err);
    } finally {
      setLoading(false);
    }
  }, [test.id]);

  useEffect(() => {
    loadTestSkis();
  }, [loadTestSkis]);

  if (loading) {
    return <CircularProgress size={20} />;
  }

  const rankedSkis = skis
    .filter(ski => ski.rank !== null)
    .sort((a, b) => a.rank - b.rank);

  if (rankedSkis.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No ranking data available
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {rankedSkis.map((ski, index) => (
          <React.Fragment key={ski.id}>
            {/* Ski Ranking Item */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: index === 0 ? 'success.light' : 'background.paper',
              border: index === 0 ? '2px solid' : '1px solid',
              borderColor: index === 0 ? 'success.main' : 'divider',
              minWidth: 80,
              position: 'relative'
            }}>
              {/* Winner Icon */}
              {index === 0 && (
                <Typography variant="h6" sx={{ position: 'absolute', top: -8, right: -8 }}>
                  🏆
                </Typography>
              )}

              {/* Rank Badge */}
              <Box sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: index === 0 ? 'success.main' : 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: 'bold'
              }}>
                {ski.rank}
              </Box>
              
              {/* Ski Info */}
              <Box sx={{ textAlign: 'center', minWidth: 0 }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold', 
                  fontSize: '0.8rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {ski.ski_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  fontSize: '0.7rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  maxWidth: 80
                }}>
                  {ski.wax_description}
                </Typography>
              </Box>
            </Box>

            {/* Distance Arrow */}
            {index < rankedSkis.length - 1 && ski.distance_behind_previous && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 60
              }}>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  textAlign: 'center'
                }}>
                  {ski.distance_behind_previous}cm
                </Typography>
                <Box sx={{ 
                  width: 30, 
                  height: 2, 
                  backgroundColor: 'divider',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: -4,
                    top: -3,
                    width: 0,
                    height: 0,
                    borderLeft: '4px solid',
                    borderLeftColor: 'divider',
                    borderTop: '4px solid transparent',
                    borderBottom: '4px solid transparent'
                  }
                }} />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}